import { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs-stringify';
import * as Yup from 'yup';

import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { PaginationFactory, PaginatorFactory } from '../factory';
import { api } from '../http';
import { createAdminFormSchema, updateAdminFormSchema } from '../schemas/admin-schema';
import { store } from '../store';
import { DefaultRes, SystemUserTypes } from '../types';
import _ from 'lodash';


export class SystemUserService {
    public static readonly dispatch = store.dispatch.systemUser;
    public static readonly state = store.getState();

    public static async getById(
        id: string,
        callback: (
            isValid?: boolean, 
            message?: string,
            result?: SystemUserTypes.Model,
        ) => void,
    ) {
        const { dispatch: { toggleLoading } } = SystemUserService;

        try {
            toggleLoading('getById', true);

            const { data: { result, isValid, message }} = await api.get<
                DefaultRes<SystemUserTypes.Model>,
                AxiosResponse<DefaultRes<SystemUserTypes.Model>>
            >(`/customerWebAppUser/${id}`);

            toggleLoading('getById', false);

            callback(isValid, RESPONSE_CODES[message], result);
        } catch (error: any) {
            toggleLoading('getById', false);

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<SystemUserTypes.Model>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
        }
    }

    public static async update(
        dto: SystemUserTypes.CreateArgs,
        callback: (isValid?: boolean, message?: string) => void,
    ): Promise<SystemUserTypes.Model | void> {
        const { dispatch: { toggleLoading } } = SystemUserService;

        try {
            toggleLoading('update', true);

            const { data: { result, isValid, message }} = await api.put<
                DefaultRes<SystemUserTypes.Model>,
                AxiosResponse<DefaultRes<SystemUserTypes.Model>>
            >(`/customerWebAppUser/${dto.id}`, { ..._.omit(dto, ['id'])});

            toggleLoading('update', false);

            callback(isValid, RESPONSE_CODES[message]);

            return result;
        } catch (error: any) {
            toggleLoading('update', false);

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<SystemUserTypes.Model>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
        }
    }

    public static async create(
        dto: SystemUserTypes.CreateArgsWithAssignments,
        callback: (isValid?: boolean, message?: string) => void,
    ): Promise<SystemUserTypes.Model | void> {
        const { dispatch: { toggleLoading, addPaginationItem } } = SystemUserService;

        try {
            toggleLoading('create', true);
    
            const { data: { isValid, result, message }} = await api.post<
                DefaultRes<SystemUserTypes.Model>,
                AxiosResponse<DefaultRes<SystemUserTypes.Model>>
            >(`/customerWebAppUser`, { ...dto });

            toggleLoading('create', false);

            if (isValid) {
                addPaginationItem(result);
            }

            callback(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('create', false);

            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<SystemUserTypes.Model>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
        }
    }

    public static async fetchMany(
        dto: PaginatorFactory<{}>,
        callback?: (isValid?: boolean, message?: string) => void,
        ignoreState = false,
    ): Promise<PaginationFactory<SystemUserTypes.Model> | void> {
        const { dispatch: { toggleLoading, setPagination } } = SystemUserService;

        try {
            if (!ignoreState) toggleLoading('fetchMany', true);

            const qsfy = qs({
                perPage: dto.perPage,
                page: dto?.page,
                filter: {
                    ...dto?.filter,
                },
            });

            const { data: { result: { items, page, perPage, total }, isValid, message }} = await api.get<
                DefaultRes<PaginationFactory<SystemUserTypes.Model>>,
                AxiosResponse<DefaultRes<PaginationFactory<SystemUserTypes.Model>>>
            >(`/customerWebAppUser?${qsfy}`);

            if (!ignoreState) {
                toggleLoading('fetchMany', false);

                if (isValid) {
                    setPagination({ items, page, perPage, total });
                }
            }
            
            callback && callback(isValid, RESPONSE_CODES[message]);

            return { items, page, perPage, total };
        } catch (error: any) {
            toggleLoading('fetchMany', false);
            if (error.response) {
                const ERROR_CODE = (error as AxiosError<
                    DefaultRes<SystemUserTypes.Model>
                >).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!]);
            }
            callback && callback(false, String(error));
        }
    }
};